import React, { useEffect, useState } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { AlertTriangle, Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import StaleLeadsTab from './StaleLeadsTab';
import logger from '@/lib/logger';

// Standalone page that hosts the Dormant Leads Kanban (formerly a tab inside
// the Sales CRM). Manager-only view — sales executives & team leaders no
// longer see dormant leads in their pipeline at all (the /api/leads endpoint
// filters them out for those roles), so dormant leads live exclusively here.
const ALLOWED_ROLES = ['sales_manager', 'admin', 'super_admin'];

export default function DormantLeadsPage() {
    const { user } = useAuth();
    const [teams, setTeams] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    const isAllowed = ALLOWED_ROLES.includes(user?.role);
    // Manual sweep state (replaces the previous hourly auto-archiver).
    // SMs / Admins now drive when leads get re-classified as dormant.
    const [sweeping, setSweeping] = useState(false);
    const [lastSweep, setLastSweep] = useState(null);

    const runSweep = async () => {
        if (sweeping) return;
        const ok = window.confirm(
            'Run dormant-lead sweep?\n\nThis will archive any pipeline lead with no activity for 5+ days (and inactive since May 1). You can always reassign them from the table below afterwards.'
        );
        if (!ok) return;
        setSweeping(true);
        try {
            const r = await apiClient.post('/leads/dormant/run-sweep');
            setLastSweep(r.data);
            toast.success(
                `Sweep complete — ${r.data.archived} archived (${r.data.spared} spared, ${r.data.candidates} candidates).`
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Failed to run sweep';
            toast.error(msg);
        } finally {
            setSweeping(false);
        }
    };

    useEffect(() => {
        if (!isAllowed) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const [agentsRes, teamsRes] = await Promise.all([
                    apiClient.get('/users?roles=sales_executive,team_leader,sales_manager,mentor,master_of_academics'),
                    apiClient.get('/teams?department=Sales'),
                ]);
                if (cancelled) return;
                const activeAgents = (agentsRes.data || []).filter(u => u.is_active);
                // Sort: TLs/managers first, then sales execs, then mentors
                const roleOrder = {
                    team_leader: 0, sales_manager: 0,
                    master_of_academics: 0, master_of_academics_: 0,
                    sales_executive: 1, mentor: 2,
                };
                activeAgents.sort((a, b) => (roleOrder[a.role] ?? 1) - (roleOrder[b.role] ?? 1));
                // Decorate agents with team_name for nicer dropdowns
                const teamList = teamsRes.data || [];
                const teamMap = Object.fromEntries(teamList.map(t => [t.id, t.name]));
                setAgents(activeAgents.map(a => ({ ...a, team_name: a.team_name || teamMap[a.team_id] || '' })));
                setTeams(teamList);
            } catch (e) {
                logger.error('Failed to load dormant page directory:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isAllowed]);

    if (!isAllowed) {
        return (
            <div className="p-6">
                <div className="max-w-md mx-auto mt-12 rounded-lg border bg-card p-6 text-center" data-testid="dormant-leads-no-access">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                    <h2 className="text-lg font-semibold">Dormant Leads is manager-only</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        This view is restricted to Sales Managers and Admins.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-4" data-testid="dormant-leads-page">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Dormant Leads
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Pipeline leads with no activity for 5 days or more (pre May 1, 2026).
                        Reassign them to a fresh owner to keep them moving — they are hidden
                        from individual executive and team-leader pipelines until you act.
                    </p>
                    {lastSweep && (
                        <p className="text-xs text-emerald-600 mt-1" data-testid="dormant-sweep-result">
                            Last sweep: {lastSweep.archived} archived · {lastSweep.spared} spared · ran by {lastSweep.ran_by}
                        </p>
                    )}
                </div>
                <Button
                    onClick={runSweep}
                    disabled={sweeping}
                    variant="default"
                    data-testid="dormant-run-sweep-btn"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                    {sweeping ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running sweep…</>
                    ) : (
                        <><Play className="h-4 w-4 mr-2" /> Run Dormant Sweep</>
                    )}
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground p-8 text-center" data-testid="dormant-leads-loading">
                    Loading directory…
                </div>
            ) : (
                <StaleLeadsTab teams={teams} agents={agents} />
            )}
        </div>
    );
}
